<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card ledger-report-card">
          <div class="card-body">
            <div class="report-header-bar">
              <div>
                <h5>Stock Ledger Report</h5>
                <p>Every stock movement (purchases, sales, returns, adjustments) with the running balance. Pick an item to follow its stock from opening to closing.</p>
              </div>
              <div v-if="canExport" class="export-actions">
                <button class="btn btn-outline-success" :disabled="!!exporting" @click="exportLedger('excel')">
                  <vue-feather type="download" size="15"></vue-feather>
                  {{ exporting === 'excel' ? 'Preparing...' : 'Excel' }}
                </button>
                <button class="btn btn-outline-danger" :disabled="!!exporting" @click="exportLedger('pdf')">
                  <vue-feather type="file-text" size="15"></vue-feather>
                  {{ exporting === 'pdf' ? 'Preparing...' : 'PDF' }}
                </button>
              </div>
            </div>

            <div class="report-toolbar">
              <div class="filter-grid">
                <div class="form-group">
                  <label>From Date</label>
                  <b-form-input v-model="filters.fromDate" type="date" />
                </div>
                <div class="form-group">
                  <label>To Date</label>
                  <b-form-input v-model="filters.toDate" type="date" />
                </div>
                <div class="form-group">
                  <label>Item</label>
                  <b-form-select v-model="filters.itemid" :options="itemOptions" />
                </div>
                <div class="form-group">
                  <label>Transaction Type</label>
                  <b-form-select v-model="filters.transactionType" :options="transactionTypeOptions" />
                </div>
                <div class="button-group">
                  <button class="btn btn-submit" :disabled="loading" @click="applyFilters">{{ loading ? 'Loading...' : 'View Report' }}</button>
                  <button class="btn btn-outline-secondary" @click="resetFilters">Reset</button>
                </div>
              </div>
              <ReportDatePresets :from="filters.fromDate" :to="filters.toDate" @select="applyPeriod" />
            </div>

            <div class="summary-strip">
              <div>
                <span>Opening Qty</span>
                <strong>{{ formatNumber(summary.opening) }}</strong>
              </div>
              <div>
                <span>Qty In</span>
                <strong>{{ formatNumber(summary.in) }}</strong>
              </div>
              <div>
                <span>Qty Out</span>
                <strong>{{ formatNumber(summary.out) }}</strong>
              </div>
              <div>
                <span>Closing Qty</span>
                <strong>{{ formatNumber(summary.closing) }}</strong>
              </div>
            </div>

            <div class="table-responsive">
              <a-table
                :columns="columns"
                :data-source="rows"
                :loading="loading"
                :pagination="paginationConfig"
                row-key="rowkey"
                @change="handleTableChange"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'date'">
                    {{ formatDate(record.date) }}
                  </template>
                  <template v-else-if="column.key === 'type'">
                    <span class="type-pill">{{ formatType(record.type) }}</span>
                  </template>
                  <template v-else-if="column.key === 'refno'">
                    <button
                      v-if="canOpenReference(record)"
                      type="button"
                      class="ref-link"
                      @click.stop="openReferenceModal(record)"
                    >
                      {{ record.refno }}
                    </button>
                    <span v-else>{{ record.refno || "-" }}</span>
                  </template>
                  <template v-else-if="column.key === 'item'">
                    <div class="item-cell">
                      <strong>{{ record.itemname }}</strong>
                      <span>{{ record.itemcode }} - {{ record.categoryname || "-" }}</span>
                    </div>
                  </template>
                  <template v-else-if="['in', 'out', 'balance'].includes(column.key)">
                    <span class="qty-text">{{ formatNumber(record[column.key]) }}</span>
                  </template>
                </template>
              </a-table>
            </div>
          </div>
        </div>

        <Teleport to="body">
          <div
            v-if="showReferenceModal"
            class="reference-modal-backdrop"
            @click.self="closeReferenceModal"
          >
          <div class="reference-modal" role="dialog" aria-modal="true" aria-labelledby="reference-modal-title">
            <div class="reference-modal-header">
              <div>
                <h5 id="reference-modal-title">{{ referenceModalTitle }}</h5>
                <p v-if="referenceModalRecord?.refno">Bill No: {{ referenceModalRecord.refno }}</p>
              </div>
              <button type="button" class="reference-modal-close" @click="closeReferenceModal" aria-label="Close">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <div v-if="referenceModalLoading" class="reference-loading">Loading bill details...</div>
            <div v-else-if="referenceModalError" class="reference-error">{{ referenceModalError }}</div>
            <div v-else-if="referenceModalDetail" class="reference-detail">
              <div class="reference-summary">
                <div>
                  <span>Bill No</span>
                  <strong>{{ referenceModalDetail.billno || referenceModalRecord?.refno || "-" }}</strong>
                </div>
                <div>
                  <span>Date</span>
                  <strong>{{ formatDateOnly(referenceDate(referenceModalDetail)) }}</strong>
                </div>
                <div>
                  <span>Party</span>
                  <strong>{{ referenceParty(referenceModalDetail) }}</strong>
                </div>
                <div>
                  <span>Net Amount</span>
                  <strong>Rs. {{ formatNumber(referenceModalDetail.netamount) }}</strong>
                </div>
              </div>

              <div class="table-responsive mt-3">
                <table class="table table-sm reference-items-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th class="text-end">Qty</th>
                      <th class="text-end">Rate</th>
                      <th class="text-end">Tax</th>
                      <th class="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in referenceItems(referenceModalDetail)" :key="item.id || index">
                      <td>
                        <strong>{{ item.itemname || item.itemid || "-" }}</strong>
                        <span v-if="item.batchno" class="d-block text-muted">Batch: {{ item.batchno }}</span>
                      </td>
                      <td class="text-end">{{ formatNumber(item.quantity ?? item.returnqty ?? 0) }}</td>
                      <td class="text-end">Rs. {{ formatNumber(item.rate ?? item.purchaseprice ?? 0) }}</td>
                      <td class="text-end">Rs. {{ formatNumber(item.taxamount) }}</td>
                      <td class="text-end">Rs. {{ formatNumber(item.amount ?? lineAmount(item)) }}</td>
                    </tr>
                    <tr v-if="!referenceItems(referenceModalDetail).length">
                      <td colspan="5" class="text-center text-muted">No items found.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="reference-modal-actions">
              <button type="button" class="btn btn-primary" @click="closeReferenceModal">Close</button>
            </div>
          </div>
          </div>
        </Teleport>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { downloadReport, errorMessage } from "@/services/reports";
import ReportDatePresets from "@/components/report/ReportDatePresets.vue";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const columns = [
  { title: "Date", dataIndex: "date", key: "date", width: 150 },
  { title: "Type", dataIndex: "type", key: "type", width: 170 },
  { title: "Ref No", dataIndex: "refno", key: "refno", width: 180 },
  { title: "Item", key: "item", width: 260 },
  { title: "In", dataIndex: "in", key: "in", align: "right", width: 110 },
  { title: "Out", dataIndex: "out", key: "out", align: "right", width: 110 },
  { title: "Balance", dataIndex: "balance", key: "balance", align: "right", width: 120 },
  { title: "U.O.M", dataIndex: "uom", key: "uom", width: 90 },
];

export default {
  name: "stockledgerreport",
  components: { ReportDatePresets },
  data() {
    return {
      title: "Stock Ledger Report",
      title1: "Item wise stock movement",
      exporting: "",
      rows: [],
      items: [],
      loading: false,
      filters: {
        fromDate: this.monthStart(),
        toDate: this.today(),
        itemid: "",
        transactionType: "",
      },
      summary: {
        opening: 0,
        in: 0,
        out: 0,
        closing: 0,
      },
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0,
      },
      showReferenceModal: false,
      referenceModalLoading: false,
      referenceModalRecord: null,
      referenceModalDetail: null,
      referenceModalError: "",
      columns,
    };
  },
  computed: {
    canExport() {
      return this.$store.getters.hasPermission("reports.stock.export");
    },
    itemOptions() {
      return [
        { value: "", text: "All Items" },
        ...this.items.map((item) => ({
          value: item.itemid,
          text: `${item.itemname}${item.itemcode ? ` (${item.itemcode})` : ""}`,
        })),
      ];
    },
    transactionTypeOptions() {
      return [
        { value: "", text: "All Types" },
        { value: "OPENING", text: "Opening" },
        { value: "OPENING_STOCK", text: "Opening Stock" },
        { value: "PURCHASE", text: "Purchase" },
        { value: "PURCHASE_ADJUSTMENT", text: "Purchase Adjustment" },
        { value: "PURCHASE_REVERSAL", text: "Purchase Reversal" },
        { value: "PURCHASE_RETURN", text: "Purchase Return" },
        { value: "PURCHASE_RETURN_CXL", text: "Purchase Return Cancel" },
        { value: "SALE", text: "Sale" },
        { value: "SALE_REVERSAL", text: "Sale Reversal" },
        { value: "SALES_RETURN", text: "Sales Return" },
        { value: "SALES_RETURN_CANCEL", text: "Sales Return Cancel" },
        { value: "DAMAGED", text: "Damaged" },
        { value: "DAMAGED_CANCEL", text: "Damaged Cancel" },
        { value: "ADJUSTMENT_IN", text: "Stock Adjustment (In)" },
        { value: "ADJUSTMENT_OUT", text: "Stock Adjustment (Out)" },
        { value: "ADJUSTMENT_CANCEL", text: "Stock Adjustment Cancel" },
      ];
    },
    paginationConfig() {
      return {
        current: this.pagination.current,
        pageSize: this.pagination.pageSize,
        total: this.pagination.total,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} entries`,
      };
    },
    referenceModalTitle() {
      const kind = this.resolveReferenceKind(this.referenceModalRecord || {});
      return `${this.formatType(kind || "Reference")} Details`;
    },
  },
  created() {
    this.fetchItems();
    this.fetchReport();
  },
  methods: {
    async exportLedger(format) {
      // Standard report export (same layout as every report), with the filters on screen.
      this.exporting = format;
      try {
        await downloadReport("stock-ledger", {
          from_date: this.filters.fromDate,
          to_date: this.filters.toDate,
          itemid: this.filters.itemid,
          transaction_type: this.filters.transactionType,
        }, format);
      } catch (error) {
        Swal.fire({ icon: "error", title: "Export failed", text: await errorMessage(error) });
      } finally {
        this.exporting = "";
      }
    },
    today() {
      const now = new Date();
      return this.toDateInput(now);
    },
    monthStart() {
      const now = new Date();
      return this.toDateInput(new Date(now.getFullYear(), now.getMonth(), 1));
    },
    toDateInput(date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    },
    async fetchItems() {
      try {
        const response = await axios.get("/v1/items");
        this.items = Array.isArray(response.data) ? response.data : response.data.items || [];
      } catch (error) {
        console.error("Failed to load items", error);
      }
    },
    async fetchReport() {
      this.loading = true;
      try {
        const response = await axios.get("/v1/stockledgerreport", {
          params: {
            from_date: this.filters.fromDate,
            to_date: this.filters.toDate,
            itemid: this.filters.itemid || undefined,
            transaction_type: this.filters.transactionType || undefined,
            page: this.pagination.current,
            page_size: this.pagination.pageSize,
          },
        });
        this.rows = response.data.items || [];
        this.summary = response.data.summary || this.summary;
        this.pagination = {
          current: response.data.page || this.pagination.current,
          pageSize: response.data.page_size || this.pagination.pageSize,
          total: response.data.total || 0,
        };
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to load stock ledger report",
          text: error.response?.data?.detail || error.message,
        });
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.pagination.current = 1;
      this.fetchReport();
    },
    applyPeriod({ from, to }) {
      this.filters.fromDate = from;
      this.filters.toDate = to;
      this.applyFilters();
    },
    resetFilters() {
      this.filters = {
        fromDate: this.monthStart(),
        toDate: this.today(),
        itemid: "",
        transactionType: "",
      };
      this.pagination.current = 1;
      this.fetchReport();
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.fetchReport();
    },
    resolveReferenceKind(record) {
      if (record.referencekind) return record.referencekind;

      const type = String(record.type || "").toUpperCase();
      if (["OPENING", "OPENING_STOCK", "PURCHASE", "PURCHASE_ADJUSTMENT", "PURCHASE_REVERSAL"].includes(type)) {
        return "purchase";
      }
      if (["SALE", "SALE_REVERSAL"].includes(type)) {
        return "sale";
      }
      if (["PURCHASE_RETURN", "PURCHASE_RETURN_CXL"].includes(type)) {
        return "purchase_return";
      }
      if (["SALES_RETURN", "SALES_RETURN_CANCEL", "DAMAGED", "DAMAGED_CANCEL"].includes(type)) {
        return "sales_return";
      }
      return "";
    },
    getReferenceId(record) {
      return record?.referenceid || record?.referenceId || record?.ReferenceID || null;
    },
    canOpenReference(record) {
      return Boolean(this.getReferenceId(record) && record.refno && this.referenceEndpoint(record));
    },
    referenceEndpoint(record) {
      const referenceId = this.getReferenceId(record);
      if (!referenceId) return null;

      const referenceKind = this.resolveReferenceKind(record);
      const endpointByKind = {
        purchase: `/v1/purchases/${referenceId}`,
        sale: `/v1/sales/${referenceId}`,
        purchase_return: `/v1/purchase-returns/${referenceId}`,
        sales_return: `/v1/sales-returns/${referenceId}`,
      };
      return endpointByKind[referenceKind] || null;
    },
    closeReferenceModal() {
      this.showReferenceModal = false;
      this.referenceModalLoading = false;
      this.referenceModalRecord = null;
      this.referenceModalDetail = null;
      this.referenceModalError = "";
    },
    async openReferenceModal(record) {
      const endpoint = this.referenceEndpoint(record);
      if (!endpoint) return;

      this.referenceModalRecord = record;
      this.referenceModalDetail = null;
      this.referenceModalError = "";
      this.referenceModalLoading = true;
      this.showReferenceModal = true;

      try {
        const response = await axios.get(endpoint);
        this.referenceModalDetail = response.data || {};
      } catch (error) {
        this.referenceModalError = error.response?.data?.detail || error.message || "Failed to load bill details.";
      } finally {
        this.referenceModalLoading = false;
      }
    },
    referenceDate(detail) {
      return detail.billdate || detail.invoicedate || detail.returndate || detail.createdat;
    },
    referenceParty(detail) {
      return detail.vendorname || detail.customername || detail.customer || detail.vendor || "Walk-in / System";
    },
    referenceItems(detail) {
      return Array.isArray(detail.items) ? detail.items : [];
    },
    lineAmount(item) {
      const qty = Number(item.quantity ?? item.returnqty ?? 0);
      const rate = Number(item.rate ?? item.purchaseprice ?? 0);
      return qty * rate;
    },
    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatDateOnly(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    formatType(value) {
      return String(value || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    formatNumber(value) {
      // Quantities: whole numbers without ".00", fractions up to 2 decimals.
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped src="./report-theme.css"></style>
<style scoped>
.item-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.item-cell strong {
  color: #0f172a;
}
.item-cell span {
  color: #64748b;
  font-size: 12px;
}
.type-pill {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 8px;
  background: #e8f2ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 700;
}
.ref-link {
  border: 0;
  background: transparent;
  padding: 0;
  color: #0f62fe;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}
.reference-loading {
  padding: 28px;
  text-align: center;
  color: #64748b;
}
.reference-error {
  padding: 16px;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}
.reference-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.reference-modal {
  width: min(100%, 960px);
  max-height: calc(100vh - 32px);
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);
}
.reference-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.reference-modal-header h5 {
  margin: 0;
  color: #0f172a;
}
.reference-modal-header p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 13px;
}
.reference-modal-close {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 8px;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
}
.reference-modal-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
.reference-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(150px, 1fr));
  gap: 12px;
}
.reference-summary div {
  border: 1px solid #eef2f7;
  border-radius: 8px;
  padding: 10px 12px;
  background: #f8fafc;
}
.reference-summary span {
  display: block;
  color: #64748b;
  font-size: 12px;
}
.reference-summary strong {
  color: #0f172a;
  font-size: 14px;
}
.reference-items-table th {
  background: #f8fafc;
  color: #475569;
}
@media (max-width: 992px) {
  .filter-grid,
  .summary-strip,
  .reference-summary {
    grid-template-columns: 1fr;
  }
}
</style>
